import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { withFormik } from "formik";
import { useTranslation } from "react-i18next";
import {
  dataModel,
  validationDataModel,
  setInitialValues,
} from "./evaluationForm.data.form";
import Breadcrumb from "../../../utils/breadCrumbs";
import PATHS from "../../../../constants/paths";
import { POINTS_ALL } from "../../../../constants/index";
import { Tooltip } from "react-tooltip";
import { Can } from "../../../security/permissions/Can";
import { USER_PERMISSIONS } from "../../../security/permissions/rolesPermission";
import {
  Subtitle,
  SectionCard,
  SectionHeader,
  SectionTitle,
  WeightProgressContainer,
  WeightLabel,
  WeightText,
  WeightValue,
  ProgressBar,
  ProgressFill,
  ObjectiveItem,
  ObjectiveHeader,
  ObjectiveNumber,
  ObjectiveTitle,
  ObjectiveValidationIcon,
  ObjectiveWeight,
  ExpandIcon,
  ObjectiveContent,
  FormGroup,
  Label,
  TextArea,
  WeightSliderContainer,
  WeightInput,
  WeightSlider,
  CharacterCount,
  InfoIcon,
  RequiredBadge,
  ButtonGroup,
  PrimaryButton,
  SecondaryButton,
  SkillItem,
  SkillWeight,
  SkillName,
  FinalEvaluationCard,
  FinalEvaluationLabel,
  FinalEvaluationValue,
  CheckIcon,
  EvaluationResultsContainer,
  EvaluationResultCard,
  EvaluationResultHeader,
  EvaluationResultIcon,
  EvaluationResultTitle,
  EvaluationResultScore,
  EvaluationResultLabel,
  EvaluationComment,
  EvaluationCommentLabel,
  EvaluationCommentText,
  NoEvaluationMessage,
  EvaluationFormContainer,
  EvaluationFormResultLabel,
  AssessmentRating,
  RatingBadge,
  BreadcrumbDiv,
  PageHeader,
  InfoUser,
  UserTitle,
  FinalEvaluationCardNew,
  FinalEvaluationValueNew,
} from "./evaluationForm.modern.styled";

const ModernEvaluationForm = (props) => {
  const { t } = useTranslation();
  const {
    values,
    setFieldValue,
    handleSubmit,
    cancelAction,
    dataForm,
    edit,
    evaluation,
    onlyView,
  } = props;

  const [expandedObjectives, setExpandedObjectives] = useState({
    1: true,
    2: false,
    3: false,
    4: false,
    5: false,
  });

  const [weights, setWeights] = useState({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });
  const [totalWeight, setTotalWeight] = useState(0);
  const [characterCounts, setCharacterCounts] = useState({
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  });

  const breads = [
    { label: t("home"), path: PATHS.HOME },
    { label: t("forms.evaluation.title"), path: PATHS.EVALUATION },
  ];

  if (onlyView) {
    breads.push({ label: t("forms.evaluation.detail") });
  } else {
    if (edit) {
      breads.push({ label: t("forms.evaluation.edit") });
    } else {
      breads.push({ label: t("forms.evaluation.new") });
    }
  }

  const comments = {};
  if (evaluation && evaluation.comments && evaluation.comments.length > 0) {
    for (const comment of evaluation.comments) {
      comments[`${comment.type}${comment.obj}`] = comment.comment;
    }
  }

  useEffect(() => {
    if (edit && evaluation) {
      const newWeights = {
        1: Number(evaluation.weightCriterial01) || 0,
        2: Number(evaluation.weightCriterial02) || 0,
        3: Number(evaluation.weightCriterial03) || 0,
        4: Number(evaluation.weightCriterial04) || 0,
        5: Number(evaluation.weightCriterial05) || 0,
      };
      setWeights(newWeights);
      const total = Object.values(newWeights).reduce((sum, w) => sum + w, 0);
      setTotalWeight(total);

      // Initialize character counts
      const newCharCounts = {
        1: (evaluation.criterial01 || "").length,
        2: (evaluation.criterial02 || "").length,
        3: (evaluation.criterial03 || "").length,
        4: (evaluation.criterial04 || "").length,
        5: (evaluation.criterial05 || "").length,
      };
      setCharacterCounts(newCharCounts);
    }
  }, [edit, evaluation]);

  const toggleObjective = (index) => {
    setExpandedObjectives((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const onChangeWeight = (value, criteriaIndex) => {
    const numValue = Math.max(0, Math.min(100, Number(value) || 0));
    const newWeights = { ...weights };
    newWeights[criteriaIndex] = numValue;
    setWeights(newWeights);

    const total = Object.values(newWeights).reduce((sum, w) => sum + w, 0);
    setTotalWeight(total);

    setFieldValue(`weightCriterial0${criteriaIndex}`, numValue);
  };

  const handleTextChange = (e, criteriaIndex) => {
    const value = e.target.value;
    setFieldValue(`criterial0${criteriaIndex}`, value);
    setCharacterCounts((prev) => ({
      ...prev,
      [criteriaIndex]: value.length,
    }));
  };

  const getPoints = (point) => {
    let label;
    if (point !== undefined) {
      const info = POINTS_ALL.find((p) => p.code === point);
      if (info) {
        label = t(info.label);
      }
    } else {
      label = t("point.na");
    }
    return label;
  };

  const renderEvaluationResults = (index) => {
    const hasAutoEval = evaluation?.[`pointAuto0${index}`];
    const hasManagerEval = evaluation?.[`point0${index}`];

    if (!hasAutoEval) {
      return null;
    }
    return (
      <EvaluationResultsContainer>
        {/* Self Evaluation */}
        <EvaluationResultCard $type="auto">
          <EvaluationResultHeader>
            <EvaluationResultIcon $type="auto">
              <i className="fa-solid fa-user-pen"></i>
            </EvaluationResultIcon>
            <EvaluationResultTitle $type="auto">
              {t("forms.evaluation.labels.selfEvaluation")}
            </EvaluationResultTitle>
          </EvaluationResultHeader>
          <div className="d-flex align-items-center">
            <EvaluationResultLabel $type="auto">
              {t("forms.evaluation.labels.employeeScore")}
            </EvaluationResultLabel>
            <AssessmentRating>
              <RatingBadge $type="auto">
                {getPoints(evaluation[`pointAuto0${index}`])}
              </RatingBadge>
            </AssessmentRating>
          </div>
          {/* <EvaluationResultScore $type="auto">
            {getPoints(evaluation[`pointAuto0${index}`])}
          </EvaluationResultScore>
          <EvaluationResultLabel $type="auto">
            {t("forms.evaluation.labels.employeeScore")}
          </EvaluationResultLabel> */}

          {comments[`auto${index}`] && (
            <EvaluationComment $type="auto">
              <EvaluationCommentLabel $type="auto">
                <i className="fa-solid fa-comment"></i>
                {t("forms.evaluation.labels.employeeComment")}
              </EvaluationCommentLabel>
              <EvaluationCommentText $type="auto">
                {comments[`auto${index}`]}
              </EvaluationCommentText>
            </EvaluationComment>
          )}
        </EvaluationResultCard>

        {/* Manager Evaluation */}
        {hasManagerEval ? (
          <EvaluationFormContainer $type="manager">
            <EvaluationResultHeader>
              <EvaluationResultIcon $type="manager">
                <i className="fa-solid fa-user-tie"></i>
              </EvaluationResultIcon>
              <EvaluationResultTitle $type="manager">
                {t("forms.evaluation.labels.managerEvaluation")}
              </EvaluationResultTitle>
            </EvaluationResultHeader>
            <div className="d-flex align-items-center">
              <EvaluationResultLabel $type="eval">
                {t("forms.evaluation.labels.managerScore")}
              </EvaluationResultLabel>
              <AssessmentRating>
                <RatingBadge $type="eval">
                  {getPoints(evaluation[`point0${index}`])}
                </RatingBadge>
              </AssessmentRating>
            </div>
            {/* <EvaluationResultScore $type="manager">
              {getPoints(evaluation[`point0${index}`])}
            </EvaluationResultScore> */}
            {/* <EvaluationResultLabel $type="manager">
              {t("forms.evaluation.labels.managerScore")}
            </EvaluationResultLabel> */}
            {comments[`final${index}`] && (
              <EvaluationComment $type="manager">
                <EvaluationCommentLabel $type="manager">
                  <i className="fa-solid fa-comment"></i>
                  {t("forms.evaluation.labels.managerComment")}
                </EvaluationCommentLabel>
                <EvaluationCommentText $type="manager">
                  {comments[`final${index}`]}
                </EvaluationCommentText>
              </EvaluationComment>
            )}
          </EvaluationFormContainer>
        ) : (
          <NoEvaluationMessage>
            <i className="fa-solid fa-hourglass-half"></i>
            {t("forms.evaluation.labels.pendingManagerEvaluation")}
          </NoEvaluationMessage>
        )}
      </EvaluationResultsContainer>
    );
  };

  const renderObjective = (index) => {
    const isExpanded = expandedObjectives[index];
    const weight = weights[index];
    const charCount = characterCounts[index];
    const fieldName = `criterial0${index}`;

    // Validar si el objetivo está completo
    const objectiveText = values[fieldName] || "";
    const isObjectiveValid = objectiveText.trim().length > 0 && weight > 0;

    return (
      <ObjectiveItem key={index}>
        <ObjectiveHeader
          $expanded={isExpanded}
          onClick={() => toggleObjective(index)}
        >
          <ObjectiveNumber>#{index}</ObjectiveNumber>
          <ObjectiveTitle>
            {t(`forms.evaluation.labels.criterial0${index}`)}
            <ObjectiveValidationIcon
              $isValid={isObjectiveValid}
              className={
                isObjectiveValid
                  ? "fas fa-check-circle"
                  : "fas fa-exclamation-circle"
              }
              data-tooltip-id={`tooltip-validation-${index}`}
            />
            <Tooltip id={`tooltip-validation-${index}`}>
              <div style={{ maxWidth: "200px" }}>
                {isObjectiveValid
                  ? t("forms.evaluation.labels.objectiveComplete")
                  : t("forms.evaluation.labels.objectiveIncomplete")}
              </div>
            </Tooltip>
          </ObjectiveTitle>
          <ObjectiveWeight>{weight}%</ObjectiveWeight>
          <ExpandIcon $expanded={isExpanded} className="fas fa-chevron-down" />
        </ObjectiveHeader>

        <ObjectiveContent $expanded={isExpanded}>
          <FormGroup>
            <Label>
              {t("forms.evaluation.labels.objectiveDefinition")}
              <RequiredBadge>*</RequiredBadge>
              <InfoIcon
                className="fas fa-info-circle"
                data-tooltip-id={`tooltip-obj-${index}`}
                style={{ marginLeft: "6px" }}
              />
            </Label>
            <Tooltip id={`tooltip-obj-${index}`}>
              <div style={{ maxWidth: "300px" }}>
                {t("forms.evaluation.labels.objectiveDefinitionTooltip")}
              </div>
            </Tooltip>
            <TextArea
              id={fieldName}
              name={fieldName}
              value={values[fieldName] || ""}
              onChange={(e) => handleTextChange(e, index)}
              placeholder={t(
                "forms.evaluation.labels.objectiveDefinitionPlaceholder"
              )}
              readOnly={onlyView}
              maxLength={1000}
            />
            <CharacterCount $error={charCount > 1000}>
              {charCount} / 1000 {t("forms.evaluation.labels.characters")}
            </CharacterCount>
          </FormGroup>

          {!onlyView && (
            <>
              <FormGroup>
                <Label>
                  {t("forms.evaluation.labels.weightPercent")}
                  <RequiredBadge>*</RequiredBadge>
                </Label>
                <WeightSliderContainer>
                  <WeightInput
                    type="number"
                    min="0"
                    max="100"
                    value={weight}
                    onChange={(e) => onChangeWeight(e.target.value, index)}
                    readOnly={onlyView}
                  />
                  <WeightSlider
                    type="range"
                    min="0"
                    max="100"
                    value={weight}
                    onChange={(e) => onChangeWeight(e.target.value, index)}
                    disabled={onlyView}
                  />
                </WeightSliderContainer>
              </FormGroup>
            </>
          )}

          {/* Show evaluations if they exist */}
          {renderEvaluationResults(index)}
        </ObjectiveContent>
      </ObjectiveItem>
    );
  };

  const renderSkills = () => {
    let listSkills = [];
    if (edit) {
      listSkills = evaluation.professionalSkill || [];
    } else {
      listSkills = dataForm.skills || [];
    }

    return listSkills.map((skill, index) => (
      <div key={`skill_${index}`} style={{ marginBottom: "16px" }}>
        <SkillItem>
          <SkillWeight>{skill.weight}%</SkillWeight>
          <SkillName>
            {t(`skill:skill_${index + 1}.name`)}
            <InfoIcon
              className="fas fa-info-circle"
              data-tooltip-id={`tooltip-skill-${index}`}
            />
          </SkillName>
          <Tooltip id={`tooltip-skill-${index}`}>
            <div style={{ maxWidth: "300px" }}>
              {t(`skill:skill_${index + 1}.description`)}
            </div>
          </Tooltip>
        </SkillItem>

        {/* Render skill evaluations with modern cards */}
        {skill?.autoPoints && (
          <EvaluationResultsContainer>
            <EvaluationResultCard $type="auto">
              <EvaluationResultHeader>
                <EvaluationResultIcon $type="auto">
                  <i className="fa-solid fa-user-pen"></i>
                </EvaluationResultIcon>
                <EvaluationResultTitle $type="auto">
                  {t("forms.evaluation.labels.selfEvaluation")}
                </EvaluationResultTitle>
              </EvaluationResultHeader>
              {/* prueba */}
              <div className="d-flex align-items-center">
                <EvaluationResultLabel $type="auto">
                  {t("forms.evaluation.labels.employeeScore")}
                </EvaluationResultLabel>
                <AssessmentRating>
                  <RatingBadge $type="auto">
                    {getPoints(skill.autoPoints)}
                  </RatingBadge>
                </AssessmentRating>
              </div>
              {/* aqui */}
              {/* <EvaluationResultScore $type="auto">
                {getPoints(skill.autoPoints)}
              </EvaluationResultScore>
              <EvaluationResultLabel $type="auto">
                {t("forms.evaluation.labels.employeeScore")}
              </EvaluationResultLabel> */}
              {comments[`autoSkill${index + 1}`] && (
                <EvaluationComment $type="auto">
                  <EvaluationCommentLabel $type="auto">
                    <i className="fa-solid fa-comment"></i>
                    {t("forms.evaluation.labels.employeeComment")}
                  </EvaluationCommentLabel>
                  <EvaluationCommentText $type="auto">
                    {comments[`autoSkill${index + 1}`]}
                  </EvaluationCommentText>
                </EvaluationComment>
              )}
            </EvaluationResultCard>

            {skill?.points ? (
              <EvaluationResultCard $type="manager">
                <EvaluationResultHeader>
                  <EvaluationResultIcon $type="manager">
                    <i className="fa-solid fa-user-tie"></i>
                  </EvaluationResultIcon>
                  <EvaluationResultTitle $type="manager">
                    {t("forms.evaluation.labels.managerEvaluation")}
                  </EvaluationResultTitle>
                </EvaluationResultHeader>
                <div className="d-flex align-items-center">
                  <EvaluationResultLabel $type="eval">
                    {t("forms.evaluation.labels.managerScore")}
                  </EvaluationResultLabel>
                  <AssessmentRating>
                    <RatingBadge $type="eval">
                      {getPoints(skill.points)}
                    </RatingBadge>
                  </AssessmentRating>
                </div>
                {/* <EvaluationResultScore $type="manager">
                  {getPoints(skill.points)}
                </EvaluationResultScore>
                <EvaluationResultLabel $type="manager">
                  {t("forms.evaluation.labels.managerScore")}
                </EvaluationResultLabel> */}
                {comments[`finalSkill${index + 1}`] && (
                  <EvaluationComment $type="manager">
                    <EvaluationCommentLabel $type="manager">
                      <i className="fa-solid fa-comment"></i>
                      {t("forms.evaluation.labels.managerComment")}
                    </EvaluationCommentLabel>
                    <EvaluationCommentText $type="manager">
                      {comments[`finalSkill${index + 1}`]}
                    </EvaluationCommentText>
                  </EvaluationComment>
                )}
              </EvaluationResultCard>
            ) : (
              <NoEvaluationMessage>
                <i className="fa-solid fa-hourglass-half"></i>
                {t("forms.evaluation.labels.pendingManagerEvaluation")}
              </NoEvaluationMessage>
            )}
          </EvaluationResultsContainer>
        )}
      </div>
    ));
  };

  const isWeightComplete = totalWeight === 100;
  const isWeightError = totalWeight > 100;

  return (
    <>
      {onlyView && (
        // <div className="row justify-content-center mt-4 mt-md-5">
        //   <div className="col-12 col-md-11 marg-btm-1rem marg-top-40px">
        //     <h3 className="title">{`${t("forms.evaluation.detail")}: ${
        //       evaluation
        //         ? `${evaluation.dataUser.name} ${evaluation.dataUser.surname}`
        //         : ""
        //     }`}</h3>
        //   </div>
        // </div>
        <div className="row justify-content-center mt-4 mt-md-5">
          <div className="col-12 col-md-11 marg-btm-1rem marg-top-40px">
            <PageHeader className="col-12 col-md-11 marg-btm-1rem marg-top-40px">
              <InfoUser>
                <UserTitle>
                  <h4>{`${t("forms.evaluation.detail")}: ${
                    evaluation
                      ? `${evaluation.dataUser.name} ${evaluation.dataUser.surname}`
                      : ""
                  }`}</h4>
                </UserTitle>

                <BreadcrumbDiv>
                  <Breadcrumb items={breads} />
                </BreadcrumbDiv>
              </InfoUser>
              {evaluation?.finalPoints && evaluation?.finalPoints !== "-" && (
                <Can do={USER_PERMISSIONS.VIEW_FINAL_POINT}>
                  {/* <div className="row justify-content-end ">
                    <div className="col-12 col-md-11 marg-btm-1rem marg-top-40px"> */}
                  <FinalEvaluationCardNew>
                    {/* <FinalEvaluationLabel>
                          {t("forms.evaluation.labels.finalEvaluation")}
                        </FinalEvaluationLabel> */}
                    <div className="m-2">
                      <FinalEvaluationValueNew>
                        {evaluation.finalPoints} (
                        {t(`rating.${evaluation.finalPoints}`)})
                      </FinalEvaluationValueNew>
                    </div>
                  </FinalEvaluationCardNew>
                  {/* </div>
                  </div> */}
                </Can>
              )}
            </PageHeader>
          </div>
        </div>
      )}
      {!onlyView && (
        // <div className="row justify-content-center mt-4 mt-md-5">
        //   <div className="col-12 col-md-11 marg-btm-1rem marg-top-40px">
        //     {edit ? (
        //       <h3 className="title">{`${t("forms.evaluation.edit")}: ${
        //         evaluation
        //           ? `${evaluation.dataUser.name} ${evaluation.dataUser.surname}`
        //           : ""
        //       }`}</h3>
        //     ) : (
        //       <h3 className="title">{`${t("forms.evaluation.new")}: ${
        //         dataForm?.user
        //           ? `${dataForm.user.name} ${dataForm.user.surname}`
        //           : ""
        //       }`}</h3>
        //     )}
        //   </div>
        // </div>
        <div className="row justify-content-center mt-4 mt-md-5">
          <div className="col-12 col-md-11 marg-btm-1rem marg-top-40px">
            <PageHeader className="col-12 col-md-11 marg-btm-1rem marg-top-40px">
              {edit ? (
                <h3>{`${t("forms.evaluation.edit")}: ${
                  evaluation
                    ? `${evaluation.dataUser.name} ${evaluation.dataUser.surname}`
                    : ""
                }`}</h3>
              ) : (
                <h3>{`${t("forms.evaluation.new")}: ${
                  dataForm?.user
                    ? `${dataForm.user.name} ${dataForm.user.surname}`
                    : ""
                }`}</h3>
              )}
              <BreadcrumbDiv>
                <Breadcrumb items={breads} />
              </BreadcrumbDiv>
            </PageHeader>
          </div>
        </div>
      )}

      <div className="row justify-content-center">
        <div className="col-12">
          {/* {evaluation?.finalPoints && evaluation?.finalPoints !== "-" && (
            <Can do={USER_PERMISSIONS.VIEW_FINAL_POINT}>
              <div className="row justify-content-center ">
                <div className="col-12 col-md-11 marg-btm-1rem marg-top-40px">
                  <FinalEvaluationCard>
                    <FinalEvaluationLabel>
                      {t("forms.evaluation.labels.finalEvaluation")}
                    </FinalEvaluationLabel>
                    <FinalEvaluationValue>
                      {evaluation.finalPoints} (
                      {t(`rating.${evaluation.finalPoints}`)})
                    </FinalEvaluationValue>
                  </FinalEvaluationCard>
                </div>
              </div>
            </Can>
          )} */}

          <form onSubmit={handleSubmit} autoComplete="off">
            {/* Specific Objectives Section */}
            <div className="row justify-content-center">
              <div className="col-12 col-md-11">
                <SectionCard>
                  <SectionHeader>
                    <SectionTitle>
                      <i className="fas fa-bullseye"></i>
                      {t("forms.evaluation.labels.specificObjectivesTitle")}
                    </SectionTitle>
                  </SectionHeader>
                  <Subtitle style={{ marginBottom: "16px" }}>
                    {t("forms.evaluation.labels.specificObjectivesSubtitle")}
                  </Subtitle>{" "}
                  <WeightProgressContainer>
                    <WeightLabel>
                      <WeightText>
                        {t("forms.evaluation.labels.totalWeightAllocation")}
                      </WeightText>
                      <WeightValue
                        $complete={isWeightComplete}
                        $error={isWeightError}
                      >
                        {totalWeight}%
                        {isWeightComplete && (
                          <CheckIcon className="fas fa-check-circle" />
                        )}
                        {isWeightError && (
                          <i className="fas fa-exclamation-circle"></i>
                        )}
                      </WeightValue>
                    </WeightLabel>
                    <ProgressBar>
                      <ProgressFill
                        $percentage={totalWeight}
                        $complete={isWeightComplete}
                        $error={isWeightError}
                      />
                    </ProgressBar>
                    {isWeightError && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#ef4444",
                          marginTop: "4px",
                        }}
                      >
                        {t("forms.evaluation.maxWeight")}
                      </div>
                    )}
                  </WeightProgressContainer>
                  {[1, 2, 3, 4, 5].map((index) => renderObjective(index))}
                </SectionCard>
              </div>
            </div>

            {/* Professional Competencies Section */}
            {(dataForm?.skills || evaluation?.professionalSkill) && (
              <div className="row justify-content-center">
                <div className="col-12 col-md-11">
                  <SectionCard>
                    <SectionHeader>
                      <SectionTitle>
                        <i className="fas fa-award"></i>
                        {t(
                          "forms.evaluation.labels.professionalCompetenciesTitle"
                        )}
                      </SectionTitle>
                    </SectionHeader>

                    <Subtitle style={{ marginBottom: "16px" }}>
                      {t(
                        "forms.evaluation.labels.professionalCompetenciesSubtitle"
                      )}
                    </Subtitle>

                    {renderSkills()}

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: "16px",
                        padding: "12px 16px",
                        background: "#f0fdf4",
                        borderRadius: "8px",
                      }}
                    >
                      <span style={{ fontWeight: "600", color: "#166534" }}>
                        {t("forms.evaluation.labels.totalWeightLabel")}
                      </span>
                      <span
                        style={{
                          fontWeight: "700",
                          color: "#166534",
                          fontSize: "16px",
                        }}
                      >
                        100% <CheckIcon className="fas fa-check-circle" />
                      </span>
                    </div>
                  </SectionCard>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="row justify-content-center mt-4 mb-5">
              <div className="col-12 col-md-11">
                <ButtonGroup>
                  <SecondaryButton type="button" onClick={() => cancelAction()}>
                    <i className="fas fa-times"></i>
                    {t("forms.evaluation.buttons.cancel")}
                  </SecondaryButton>
                  {!onlyView && (
                    <PrimaryButton
                      className="btn btn-lg btn-primary"
                      type="submit"
                      // disabled={isWeightError || !isWeightComplete}
                    >
                      <i className="fas fa-save"></i>
                      {t("forms.evaluation.buttons.save")}
                    </PrimaryButton>
                  )}
                </ButtonGroup>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

ModernEvaluationForm.propTypes = {
  handleSubmit: PropTypes.func,
  initialValues: PropTypes.object,
  values: PropTypes.object,
  setFieldValue: PropTypes.func,
  cancelAction: PropTypes.func,
  dataForm: PropTypes.object,
  edit: PropTypes.bool,
  evaluation: PropTypes.object,
  onlyView: PropTypes.bool,
};

const ModernEvaluationFormF = withFormik({
  mapPropsToValues: (props) => {
    if (props.initialValues) {
      return setInitialValues(props.initialValues);
    }
    return dataModel;
  },
  validationSchema: (props) => {
    return validationDataModel(props.edit);
  },
  handleSubmit: (values, { props, resetForm }) => {
    props.onSubmit(values, resetForm);
  },
  enableReinitialize: true,
  displayName: "ModernEvaluationForm",
})(ModernEvaluationForm);

export default ModernEvaluationFormF;
